import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import { PatternQuestionOptions, ProblemData } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";

/**
 * Number patterns - skip counting and sequences. See ADR 0032 §5.
 *
 * The decision that shapes this file is where the blank goes.
 * `3, 6, 9, 12, ___` can be answered by adding to the last term without ever
 * noticing what the pattern is; `3, 6, 9, ___, 15` cannot. So the blank goes
 * anywhere except the first two positions - with fewer than two terms shown
 * there is no pattern to see, which is a correctness rule rather than a style
 * one - and `PatternGenerator.test.ts` asserts its position actually varies.
 *
 * Sequences stay non-negative and whole. Integers exist now (ADR 0030), and a
 * decreasing pattern that crosses zero is a legitimate seventh-grade question,
 * but it belongs to whatever ADR retro-fits negatives into existing formats.
 * That is why a descending sequence is built from a first term large enough
 * that the last one cannot go below zero, rather than being drawn and checked.
 */

/** The marker the renderer swaps for a rule or for the answer. */
export const PATTERN_BLANK = "___";

/** Kept small enough that a geometric sequence does not run into six digits. */
const LARGEST_TERM = 10000;

function questionTypes(question: PatternQuestionOptions): PatternQuestionOptions[] {
    if (question === PatternQuestionOptions.Mixed) {
        return [
            PatternQuestionOptions.SkipCounting,
            PatternQuestionOptions.Arithmetic,
            PatternQuestionOptions.Geometric,
        ];
    }
    return [question];
}

function usableLength(length: number): number {
    return Math.min(10, Math.max(4, Math.floor(length) || 5));
}

/**
 * Skip counting: the multiples of one number, in order, starting at it.
 *
 * There are exactly eleven of these at any length - one per step from 2 to 12
 * - which is the smallest space any question type here has. A page asking for
 * more than eleven has to repeat one, so `/skip-counting-worksheets` asks for
 * ten.
 */
function skipCounting(length: number): number[] {
    const step = getRandomInt(2, 12);
    return Array.from({ length }, (_, i) => step * (i + 1));
}

/**
 * An arithmetic sequence, ascending or descending.
 *
 * A descending one starts high enough that the final term is still positive -
 * built that way rather than drawn and rejected, so there is no failure path.
 */
function arithmetic(length: number): number[] {
    const step = getRandomInt(2, 12);
    const descending = getRandomInt(0, 1) === 0;

    if (descending) {
        const floor = step * (length - 1);
        const start = floor + getRandomInt(1, 20);
        return Array.from({ length }, (_, i) => start - step * i);
    }

    const start = getRandomInt(1, 20);
    return Array.from({ length }, (_, i) => start + step * i);
}

/**
 * A doubling or tripling sequence.
 *
 * The first term is bounded so the last one stays under LARGEST_TERM: at eight
 * terms a tripling sequence multiplies the start by 2,187, so a start of 5
 * would finish above ten thousand.
 *
 * The start was capped at 6 in a first draft, which left ten distinct
 * sequences at eight terms - a sixteen-problem sheet repeated some three
 * times. Twelve is the cap now, and `sequenceKey` below stops the repeats that
 * remain from hiding behind a moved blank.
 */
function geometric(length: number): number[] {
    const ratio = getRandomInt(0, 1) === 0 ? 2 : 3;
    const growth = Math.pow(ratio, length - 1);
    const largestStart = Math.max(1, Math.floor(LARGEST_TERM / growth));
    const start = getRandomInt(1, Math.min(12, largestStart));
    return Array.from({ length }, (_, i) => start * Math.pow(ratio, i));
}

/**
 * One pattern problem. Always succeeds.
 *
 * `display` is `[sequence with the blank marker in it, the missing value]`.
 * The renderer swaps the marker rather than reassembling the sequence, so the
 * worksheet and its key cannot disagree about the terms around the gap.
 */
export function buildPatternProblem(
    question: PatternQuestionOptions,
    length: number,
    key: number
): ProblemData {
    const terms = usableLength(length);
    let sequence: number[];

    switch (question) {
        case PatternQuestionOptions.Arithmetic:
            sequence = arithmetic(terms);
            break;
        case PatternQuestionOptions.Geometric:
            sequence = geometric(terms);
            break;
        default:
            sequence = skipCounting(terms);
            break;
    }

    // Never the first two: two terms is the least that shows a pattern at all.
    const blankAt = getRandomInt(2, sequence.length - 1);
    const shown = sequence.map((value, i) => (i === blankAt ? PATTERN_BLANK : String(value)));

    return {
        key,
        numbers: [],
        operator: "",
        display: [shown.join(", "), String(sequence[blankAt])],
        patternQuestion: question,
    };
}

/**
 * What counts as the same problem twice.
 *
 * The blank's position is not part of it: `1, 3, ___, 27, 81` and
 * `1, 3, 9, ___, 81` are one sequence with the gap moved, and on the answer
 * key they are identical. Keying on the prompt instead let three copies of the
 * same sequence onto one sheet.
 */
function sequenceKey(problem: ProblemData): string {
    return problem
        .display![0].split(", ")
        .map((term) => (term === PATTERN_BLANK ? problem.display![1] : term))
        .join(", ");
}

/** A page-shaped set of pattern problems. */
export function generatePatternPages(settings: ISettingsProps): ProblemData[][] | null {
    const types = questionTypes(settings.PatternQuestion ?? PatternQuestionOptions.Mixed);
    const length = settings.PatternLength ?? 6;
    return generateProblemPages({
        settings,
        choices: types,
        build: (question, key) => buildPatternProblem(question, length, key),
        fingerprint: sequenceKey,
    });
}
